import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ScreenHeader } from '../../components/layout/ScreenHeader';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import html2canvas from 'html2canvas';

import { logoImage } from '../../utils/LogoImageBase64';
import { Briefcase, FileDown } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { useAppContext } from '../../hooks/useAppContext';
import { Role } from '../../types';
import ModernQuotationDetailScreen from './ModernQuotationDetailScreen';

interface QuotationItem {
  category: 'الأفضل' | 'الجيد' | 'الاقتصادي' | 'الأدنى';
  horsepower: number;
  capacity_kw: number;
  price_per_kw: number;
  total_before_tax: number;
  vat15: number;
  total_with_tax: number;
  device_type?: string;
  device_count?: number;
  voltage?: number;
}

const QuotationDetailScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, designMode } = useAppContext();

  if (designMode === 'modern') {
    return <ModernQuotationDetailScreen />;
  }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [location, setLocation] = useState('');
  const [mobile, setMobile] = useState('');
  const [quoteNumber, setQuoteNumber] = useState<string>('');
  const [quoteDate, setQuoteDate] = useState<string>('');
  const [items, setItems] = useState<QuotationItem[]>([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchQuotation = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`${API_BASE_URL}/quotations/${id}`);
        if (!res.ok) throw new Error(`خطأ في جلب العرض: ${res.status}`);
        const data = await res.json();
        setCustomerName(data.customer_name || '');
        setLocation(data.location || '');
        setMobile(data.mobile || '');
        setQuoteNumber(String(data.quote_number || ''));
        setQuoteDate(String(data.quote_date || ''));
        setItems((data.items || []) as QuotationItem[]);
      } catch (e: any) {
        setError(e?.message || 'تعذر جلب العرض');
      } finally {
        setLoading(false);
      }
    };
    fetchQuotation();
  }, [id]);

  const generatePDF = async () => {
    const doc = new jsPDF('p', 'pt', 'a4');

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.width = '794px';
    container.style.height = '1123px';
    container.style.padding = '40px';
    container.style.direction = 'rtl';
    container.style.fontFamily = 'Cairo, Arial, sans-serif';
    container.style.color = '#353536';
    container.id = 'print-root';
    const d = new Date(quoteDate);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const arDate = d.toLocaleDateString('ar-SA');
    const projectLoc = String(location || '').replace(/\s+/g, '');
    const serial = (() => {
      const digits = String(quoteNumber || '').replace(/\D/g, '');
      const last3 = digits ? digits.slice(-3) : '';
      return last3 ? last3.padStart(3, '0') : '001';
    })();
    const formattedId = `${projectLoc}${yyyy}${mm}${dd}${serial}`;
    const formattedIdHtml = `<span class="accent-location">${projectLoc}</span>${yyyy}${mm}${dd}${serial}`;
    const headHtml = `
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;align-items:center;margin-top:18px;direction:ltr">
        <div style="text-align:left"></div>
        <div style="text-align:center"></div>
        <div></div>
      </div>
      <div style="border:1px solid #e0e0e0;border-radius:8px;padding:10px;margin-top:79px;background:rgba(255,255,255,0.9);width:86%;margin-left:auto;margin-right:auto">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12px">
          <div>اسم العميل: ${customerName || '-'}</div>
          <div>الموقع: ${location || '-'}</div>
          <div>رقم الجوال: ${mobile || '-'}</div>
          <div style="direction:rtl;unicode-bidi:embed">${formattedIdHtml}</div>
        </div>
      </div>
    `;
    const bgImgHtml = `<img src="/QSREPORT.png.png" crossorigin="anonymous" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:-1" />`;
    const fontCss = `<style>@font-face{font-family:'Cairo';src:url('/fonts/Cairo-Regular.ttf') format('truetype');font-weight:normal;font-style:normal}#print-root, #print-root * { color:#353536 !important }.accent-location{ color:#d00 !important }</style>`;
    const agreedHp = items.length === 1
      ? (items[0].horsepower ?? 0)
      : ((items.find(q => q.category === 'الأفضل')?.horsepower) ?? (items[0].horsepower ?? 0));
    const agreedType = items.length === 1
      ? (((items[0] as any).device_type) || 'رأس كهربائي')
      : ((((items.find(q => q.category === 'الأفضل') as any)?.device_type) || ((items[0] as any).device_type) || 'رأس كهربائي'));
    const agreedCount = items.length === 1
      ? (Number(((items[0] as any).device_count) || 1))
      : (Number((((items.find(q => q.category === 'الأفضل') as any)?.device_count) || ((items[0] as any).device_count) || 1)));
    const introHtml = `
      <div style="margin-top:32px;font-size:12px;line-height:1.6;font-weight:700;margin-right:64px;width:88%">
        <div style="margin-right:12px">المكرم/ ${customerName || '-'}</div>
        <div style="margin-right:12px">تحية طيبة.</div>
        <div>بناء على الاجتماع الذي عقد معكم لمناقشة آلية تشغيل عدد (<span style="color:#353536">${Number(agreedCount)}</span>) ${agreedType} بقدرة <span style="color:#353536">${Number(agreedHp || 0).toFixed(0)}</span> حصان على الطاقة الشمسية، نرفق لكم عرض السعر يتضمن الجوانب الفنية والتشغيلية</div>
      </div>
    `;
    const tableSingle = (q: any) => `
      <table style="width:100%;border-collapse:collapse;margin-top:116px;font-size:12px">
        <thead>
          <tr style="background:#f79156;color:#353536"><th style="padding:8px;text-align:center">البيان</th><th style="padding:8px;text-align:center">القيمة</th><th style="padding:8px;text-align:center"></th></tr>
        </thead>
        <tbody>
          <tr><td style="padding:8px;text-align:right">عدد الأحصنة (HP)</td><td style="padding:8px;text-align:center">${Number(q.horsepower ?? 0).toFixed(0)}</td><td></td></tr>
          <tr><td style="padding:8px;text-align:right">القدرة (kW)</td><td style="padding:8px;text-align:center">${Number(q.capacity_kw ?? 0).toFixed(0)}</td><td></td></tr>
          <tr><td style="padding:8px;text-align:right">الإجمالي قبل الضريبة</td><td style="padding:8px;text-align:center">${q.total_before_tax} ر.س</td><td></td></tr>
          <tr><td style="padding:8px;text-align:right">ضريبة القيمة المضافة 15%</td><td style="padding:8px;text-align:center">${q.vat15} ر.س</td><td></td></tr>
          <tr style="background:#f5f5f5"><td style="padding:8px;text-align:right">الإجمالي شامل الضريبة</td><td style="padding:8px;text-align:center">${q.total_with_tax} ر.س</td><td></td></tr>
        </tbody>
        <tfoot>
          <tr><td style="padding:8px;text-align:right">عدد المضخات المراد تشغيلها على الطاقة الشمسية</td><td></td><td style="padding:8px;text-align:center">${(q as any).device_count ?? 1}</td></tr>
          <tr><td style="padding:8px;text-align:right">المحول</td><td></td><td style="padding:8px;text-align:center">${(q as any).voltage ?? 380}V</td></tr>
        </tfoot>
      </table>
    `;
    const tableMulti = (arr: any[]) => `
      <table style="width:100%;border-collapse:collapse;margin-top:76px;font-size:12px">
        <thead>
          <tr style="background:#f79156;color:#353536">
            <th style="padding:8px;text-align:center">الفئة</th>
            <th style="padding:8px;text-align:center">HP</th>
            <th style="padding:8px;text-align:center">kW</th>
            <th style="padding:8px;text-align:center">الإجمالي قبل الضريبة</th>
            <th style="padding:8px;text-align:center">الإجمالي شامل الضريبة</th>
            <th style="padding:8px;text-align:center"></th>
          </tr>
        </thead>
        <tbody>
          ${arr.map(q => `
            <tr>
              <td style="padding:8px;background:#f5f5f5;text-align:center">${q.category}</td>
              <td style="padding:8px;text-align:center">${Number(q.horsepower ?? 0).toFixed(0)}</td>
              <td style="padding:8px;text-align:center">${Number(q.capacity_kw ?? 0).toFixed(0)}</td>
              <td style="padding:8px;text-align:center">${q.total_before_tax} ر.س</td>
              <td style="padding:8px;text-align:center">${q.total_with_tax} ر.س</td>
              <td></td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr><td colspan="5" style="padding:8px;text-align:right">عدد المضخات المراد تشغيلها على الطاقة الشمسية</td><td style="padding:8px;text-align:center">${(arr[0] as any)?.device_count ?? 1}</td></tr>
          <tr><td colspan="5" style="padding:8px;text-align:right">المحول</td><td style="padding:8px;text-align:center">${380}V</td></tr>
        </tfoot>
      </table>
    `;
    const paymentsHtml = ``;
    const notesHtml = ``;
    container.innerHTML = fontCss + bgImgHtml + headHtml + introHtml + (items.length === 1 ? tableSingle(items[0]) : tableMulti(items)) + paymentsHtml + notesHtml;
    document.body.appendChild(container);
    const canvas = await html2canvas(container, { scale: 2, useCORS: true });
    const dataUrl = canvas.toDataURL('image/png');
    document.body.removeChild(container);
    doc.addImage(dataUrl, 'PNG', 0, 0, pageWidth, pageHeight);
    doc.save(`quotation_${formattedId || 'offer'}.pdf`);
  };

  if (loading) {
    return (
      <div className="p-6">
        <p>جاري تحميل العرض...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const total = items.reduce((sum, it) => sum + Number(it.total_with_tax || 0), 0);

  const _d = quoteDate ? new Date(quoteDate) : new Date();
  const _yyyy = _d.getFullYear();
  const _mm = String(_d.getMonth() + 1).padStart(2, '0');
  const _dd = String(_d.getDate()).padStart(2, '0');
  const _projectLoc = String(location || '').replace(/\s+/g, '');
  const _serial = (() => {
    const digits = String(quoteNumber || '').replace(/\D/g, '');
    const last3 = digits ? digits.slice(-3) : '';
    return last3 ? last3.padStart(3, '0') : '001';
  })();

  return (
    <div className="space-y-6">
      <ScreenHeader
        icon={Briefcase}
        title="تفاصيل عرض السعر"
        colorClass="bg-nav-project"
        onBack={user?.role === Role.Admin ? "/admin-quotations" : "/quotations/new"}
      />

      <Card>
        <CardHeader><CardTitle>بيانات العميل</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="space-y-1"><div className="text-slate-500">اسم العميل</div><div className="font-semibold">{customerName || '-'}</div></div>
            <div className="space-y-1"><div className="text-slate-500">الموقع</div><div className="font-semibold">{location || '-'}</div></div>
            <div className="space-y-1"><div className="text-slate-500">رقم الجوال</div><div className="font-semibold">{mobile || '-'}</div></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>تفاصيل العرض</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {items.length === 1 ? (
              <table className="w-full text-sm text-center">
                <thead className="bg-slate-100 dark:bg-slate-700"><tr><th className="p-2 text-right">البيان</th><th>القيمة</th></tr></thead>
                <tbody>
                  <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">الفئة</td><td>{items[0].category}</td></tr>
                  <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">النوع</td><td>{(items[0] as any).device_type ?? 'رأس كهربائي'}</td></tr>
                  <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">العدد</td><td>{Number((items[0] as any).device_count ?? 1)}</td></tr>
                  <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">عدد الأحصنة (HP)</td><td>{items[0].horsepower ?? 0}</td></tr>
                  <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">القدرة (kW)</td><td>{items[0].capacity_kw}</td></tr>
                  <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">الفولت</td><td>{Number((items[0] as any).voltage ?? 380)}V</td></tr>
                  <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">السعر/كيلوواط</td><td>{items[0].price_per_kw} ر.س</td></tr>
                  <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">الإجمالي قبل الضريبة</td><td>{items[0].total_before_tax} ر.س</td></tr>
                  <tr className="border-b dark:border-slate-700"><td className="p-2 text-right">ضريبة القيمة المضافة (15%)</td><td>{items[0].vat15} ر.س</td></tr>
                  <tr className="font-bold bg-slate-50 dark:bg-slate-800/50"><td className="p-2 text-right">الإجمالي شامل الضريبة</td><td>{items[0].total_with_tax} ر.س</td></tr>
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm text-center">
                <thead className="bg-slate-100 dark:bg-slate-700">
                  <tr>
                    <th>الفئة</th>
                    <th>النوع</th>
                    <th>العدد</th>
                    <th>عدد الأحصنة (HP)</th>
                    <th>القدرة (kW)</th>
                    <th>الفولت</th>
                    <th>السعر/كيلوواط</th>
                    <th>الإجمالي شامل الضريبة</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((q, i) => (
                    <tr key={i} className="border-b dark:border-slate-700">
                      <td>{q.category}</td>
                      <td>{(q as any).device_type ?? 'رأس كهربائي'}</td>
                      <td>{Number((q as any).device_count ?? 1)}</td>
                      <td>{q.horsepower ?? 0}</td>
                      <td>{q.capacity_kw}</td>
                      <td>{Number((q as any).voltage ?? 380)}V</td>
                      <td>{q.price_per_kw} ر.س</td>
                      <td className="font-bold">{q.total_with_tax} ر.س</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 items-center">
            <div className="order-2 sm:order-1 mt-2 sm:mt-0 text-sm text-slate-600">رقم العرض: <span dir="rtl" style={{ unicodeBidi: 'embed' }}><span style={{ color: '#d00' }}>{_projectLoc}</span>{_yyyy}{_mm}{_dd}{_serial}</span> — التاريخ: {quoteDate ? new Date(quoteDate).toLocaleDateString('ar-SA') : '-'}</div>
            <div className="order-1 sm:order-2">
              <Button onClick={async () => { setExporting(true); try { await generatePDF(); } finally { setExporting(false); } }} disabled={exporting} icon={<FileDown size={16} />}>تصدير</Button>
            </div>
          </div>
          <div className="mt-2 text-sm font-semibold">الإجمالي شامل الضريبة: {total} ر.س</div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuotationDetailScreen;
